// src/user/EarningsSummary.js
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Typography, Box, Paper, Container, Button, Chip, Divider, Grid, Tooltip, Alert, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, AccountBalanceWallet, People, VideoLibrary, Payment, Schedule, SettingsApplications } from '@mui/icons-material';
import api from '../utils/axiosInstance';
import { alpha } from '@mui/material/styles';
import { API } from '../config/apiConfig';
import { portal } from '../styles/dashboardBrand';
import WelcomeHeader from '../components/WelcomeHeader';
import DashboardPageHeader from '../components/DashboardPageHeader';
import LoadingSkeleton from '../components/LoadingSkeleton';
import KYCStatusIndicator from '../components/KYCStatusIndicator';
import { normalizeKYCStatus } from '../utils/kycUtils';
import { dreameyDiamondsUserSum } from '../utils/dreameyDiamonds';
import {
  readHomeCache,
  shouldForceUserDashboardHomeNetwork,
  mergeDashboardRawFields,
} from '../utils/userDashboardHomeCache';

/** Glass-style hero for My Summary title row (dark / light aware). */
const summaryHeaderSurfaceSx = (t) => ({
  p: { xs: 2, sm: 2.5 },
  borderRadius: 3,
  position: 'relative',
  overflow: 'hidden',
  border: `1px solid ${alpha(portal.cyan, t.palette.mode === 'dark' ? 0.22 : 0.2)}`,
  background:
    t.palette.mode === 'dark'
      ? `linear-gradient(145deg, ${alpha('#1a1f30', 0.85)} 0%, ${alpha('#0f111a', 0.92)} 100%)`
      : `linear-gradient(145deg, ${alpha('#fff', 0.95)} 0%, ${alpha('#f0f7ff', 0.88)} 100%)`,
  boxShadow:
    t.palette.mode === 'dark'
      ? `0 12px 40px ${alpha('#000', 0.35)}, inset 0 1px 0 ${alpha('#fff', 0.06)}`
      : `0 8px 32px ${alpha(portal.cyan, 0.12)}`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: portal.gradientBar,
    opacity: 0.9,
  },
});

function EarningsSummary() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [kycStatus, setKycStatus] = useState(null);
  const [nextPayment, setNextPayment] = useState(null);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [availableBalance, setAvailableBalance] = useState(null);
  const [hasCNIC, setHasCNIC] = useState(false);
  const [isChild, setIsChild] = useState(false);
  const [isProfileComplete, setIsProfileComplete] = useState(true); // Default to true (no notification)
  const [streakForSummary, setStreakForSummary] = useState({ loading: true, streakDays: 0 });
  const [dreameyDiamondsTotal, setDreameyDiamondsTotal] = useState(0);

  useLayoutEffect(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    if (!token) return;
    let userId;
    try {
      userId = JSON.parse(atob(token.split('.')[1])).userId;
    } catch {
      return;
    }
    if (shouldForceUserDashboardHomeNetwork()) return;
    const raw = readHomeCache(userId)?.raw;
    if (!raw) return;

    if (raw.coins?.success) {
      setDreameyDiamondsTotal(dreameyDiamondsUserSum(raw.coins));
    }
    if (raw.earnings && raw.earnings.success !== false && raw.earnings.earnings != null) {
      setSummary(raw.earnings.earnings);
      setLoading(false);
    }
    const profileData = raw.profile?.user ?? raw.profile ?? {};
    const countryOfResidenceId =
      profileData.CountryOfResidenceId ??
      profileData.CountryOfResidenceID ??
      profileData.countryOfResidenceId ??
      null;
    setIsProfileComplete(countryOfResidenceId !== null && countryOfResidenceId !== undefined);
    const countryCode = profileData.CountryCode ?? profileData.countryCode ?? null;
    setUserProfile({
      id: userId,
      country: countryCode,
      countryCode: countryCode,
      region: profileData.Region ?? null,
    });
    setKycStatus({ verified: false, status: 'not_started' });
    if (raw.streak) {
      const data = raw.streak;
      const r = data?.data && typeof data.data === 'object' ? data.data : data;
      const streakDays = Math.max(0, Number(r?.currentStreak ?? r?.CurrentStreak ?? 0) || 0);
      setStreakForSummary({ loading: false, streakDays });
    }
    if ('walletBalance' in raw) {
      const balanceValue = Number(raw.walletBalance);
      setAvailableBalance(Number.isNaN(balanceValue) ? null : balanceValue);
    }
    const ps = raw.paymentSummary;
    if (ps && typeof ps === 'object') {
      if (ps.paymentSettings != null) setPaymentSettings(ps.paymentSettings);
      if (ps.nextPayment != null) setNextPayment(ps.nextPayment);
      if (typeof ps.hasCNIC === 'boolean') setHasCNIC(ps.hasCNIC);
    }
    if (typeof raw.isChild === 'boolean') {
      setIsChild(raw.isChild);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    if (!token) return;
    if (!shouldForceUserDashboardHomeNetwork()) {
      try {
        const userId = JSON.parse(atob(token.split('.')[1])).userId;
        const raw = readHomeCache(userId)?.raw;
        if (raw?.coins?.success) return;
      } catch {
        /* fall through */
      }
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(API.appCoinsApps, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && data?.success) {
          setDreameyDiamondsTotal(dreameyDiamondsUserSum(data));
        } else {
          setDreameyDiamondsTotal(0);
        }
      } catch {
        if (!cancelled) setDreameyDiamondsTotal(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load user profile and KYC status
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const decoded = JSON.parse(atob(token.split('.')[1]));
          const userId = decoded.userId;

          if (!shouldForceUserDashboardHomeNetwork()) {
            const raw = readHomeCache(userId)?.raw;
            if (raw?.profile) {
              const profileData = raw.profile?.user ?? raw.profile ?? {};
              const countryOfResidenceId =
                profileData.CountryOfResidenceId ??
                profileData.CountryOfResidenceID ??
                profileData.countryOfResidenceId ??
                null;
              setIsProfileComplete(countryOfResidenceId !== null && countryOfResidenceId !== undefined);
              const countryCode = profileData.CountryCode ?? profileData.countryCode ?? decoded.countryCode ?? decoded.country ?? null;
              setUserProfile({
                id: userId,
                country: countryCode,
                countryCode: countryCode,
                region: profileData.Region ?? decoded.region ?? null,
              });
              setKycStatus({ verified: false, status: 'not_started' });
              return;
            }
          }

          // Fetch full user profile from API to check completeness
          try {
            const profileResponse = await fetch(API.userProfile(userId), {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            
            if (profileResponse.ok) {
              const profileData = await profileResponse.json();
              const userData = profileData?.user || profileData || {};
              
              // Check if profile is complete (has CountryOfResidenceId)
              const countryOfResidenceId = userData.CountryOfResidenceId || 
                                           userData.CountryOfResidenceID || 
                                           userData.countryOfResidenceId || 
                                           null;
              setIsProfileComplete(countryOfResidenceId !== null && countryOfResidenceId !== undefined);
              
              // Get country code for Pakistan check
              const countryCode = userData.CountryCode || userData.countryCode || decoded.countryCode || decoded.country || null;
              
              const profile = {
                id: userId,
                country: countryCode,
                countryCode: countryCode,
                region: userData.Region || decoded.region || null,
              };
              setUserProfile(profile);
            } else {
              // Fallback to JWT token
              const profile = {
                id: userId,
                country: decoded.countryCode || decoded.country || null,
                region: decoded.region || null,
              };
              setUserProfile(profile);
              setIsProfileComplete(true); // Default to complete if can't fetch
            }
          } catch (profileError) {
            console.error('Error fetching user profile:', profileError);
            const profile = {
              id: userId,
              country: decoded.countryCode || decoded.country || null,
              region: decoded.region || null,
            };
            setUserProfile(profile);
            setIsProfileComplete(true); // Default to complete if error
          }
          
          // Set default KYC status without API call
          setKycStatus({
            verified: false,
            status: 'not_started'
          });
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
      }
    };

    loadUserProfile();
  }, []);

  useEffect(() => {
    const fetchEarnings = async () => {
      try {
        // Get userId from token
        const token = localStorage.getItem('token');
        if (!token) {
          console.error('No token found');
          setLoading(false);
          return;
        }

        const decoded = JSON.parse(atob(token.split('.')[1]));
        const userId = decoded.userId;

        if (!shouldForceUserDashboardHomeNetwork()) {
          const raw = readHomeCache(userId)?.raw;
          if (raw?.earnings && raw.earnings.success !== false && raw.earnings.earnings != null) {
            setSummary(raw.earnings.earnings);
            setLoading(false);
            return;
          }
        }

        console.log('🔍 Fetching earnings for userId:', userId);
        
        const response = await api.get(`/earnings/summary/${userId}`);
        console.log('📊 Earnings response:', response.data);
        
        if (response.data.success) {
          setSummary(response.data.earnings);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching earnings summary:', err);
        setLoading(false);
      }
    };

    fetchEarnings();
  }, []);

  useEffect(() => {
    const fetchAvailableBalance = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const userId = JSON.parse(atob(token.split('.')[1])).userId;
        if (!shouldForceUserDashboardHomeNetwork()) {
          const raw = readHomeCache(userId)?.raw;
          if (raw && 'walletBalance' in raw) {
            const balanceValue = Number(raw.walletBalance);
            setAvailableBalance(Number.isNaN(balanceValue) ? null : balanceValue);
            return;
          }
        }
        const response = await api.get(API.walletBalance);
        const balanceValue = Number(response?.data?.balance);
        const next = Number.isNaN(balanceValue) ? null : balanceValue;
        setAvailableBalance(next);
        mergeDashboardRawFields(userId, { walletBalance: next });
      } catch (error) {
        console.error('Error fetching wallet balance:', error);
        setAvailableBalance(null);
      }
    };

    fetchAvailableBalance();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setStreakForSummary({ loading: false, streakDays: 0 });
      return;
    }
    let userId;
    try {
      userId = JSON.parse(atob(token.split('.')[1])).userId;
    } catch {
      setStreakForSummary({ loading: false, streakDays: 0 });
      return;
    }
    if (!shouldForceUserDashboardHomeNetwork()) {
      const raw = readHomeCache(userId)?.raw;
      if (raw?.streak) {
        const data = raw.streak;
        const r = data?.data && typeof data.data === 'object' ? data.data : data;
        const streakDays = Math.max(0, Number(r?.currentStreak ?? r?.CurrentStreak ?? 0) || 0);
        setStreakForSummary({ loading: false, streakDays });
        return;
      }
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API.adsStreak}?userId=${encodeURIComponent(userId)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        const r = data?.data && typeof data.data === 'object' ? data.data : data;
        const streakDays = Math.max(0, Number(r?.currentStreak ?? r?.CurrentStreak ?? 0) || 0);
        if (!cancelled) setStreakForSummary({ loading: false, streakDays });
      } catch {
        if (!cancelled) setStreakForSummary({ loading: false, streakDays: 0 });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch payment settings, next payment info, and check for CNIC
  useEffect(() => {
    const fetchPaymentInfo = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const userId = JSON.parse(atob(token.split('.')[1])).userId;
        if (!shouldForceUserDashboardHomeNetwork()) {
          const ps = readHomeCache(userId)?.raw?.paymentSummary;
          if (ps && typeof ps === 'object' && 'hasCNIC' in ps) {
            if (ps.paymentSettings != null) setPaymentSettings(ps.paymentSettings);
            if (ps.nextPayment != null) setNextPayment(ps.nextPayment);
            if (typeof ps.hasCNIC === 'boolean') setHasCNIC(ps.hasCNIC);
            return;
          }
        }
        const [settingsRes, nextPayRes, accountsRes] = await Promise.all([
          api.get(API.paymentSettings),
          api.get(API.nextPayment),
          api.get(API.savedPaymentAccounts).catch(() => ({ data: { accounts: [] } })) // Don't fail if endpoint errors
        ]);
        
        if (settingsRes.data.success) {
          setPaymentSettings(settingsRes.data.data);
        }
        
        if (nextPayRes.data.success) {
          setNextPayment(nextPayRes.data.data);
        }

        // Check if user has CNIC in any JazzCash account
        const accounts = Array.isArray(accountsRes.data?.accounts) ? accountsRes.data.accounts : [];
        const jazzCashAccount = accounts.find(acc => 
          (acc.Provider || acc.PaymentMethodName) === 'JazzCash'
        );
        const hasCNICValue = jazzCashAccount?.CNIC && 
                            jazzCashAccount.CNIC.trim().length > 0;
        setHasCNIC(hasCNICValue);

        mergeDashboardRawFields(userId, {
          paymentSummary: {
            paymentSettings: settingsRes.data.success ? settingsRes.data.data : null,
            nextPayment: nextPayRes.data.success ? nextPayRes.data.data : null,
            hasCNIC: hasCNICValue,
          },
        });
      } catch (error) {
        console.error('Error fetching payment info:', error);
        setHasCNIC(false); // Default to false if error
      }
    };
    
    fetchPaymentInfo();
  }, []);

  // Check if user is a child member
  useEffect(() => {
    const checkIfChild = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const userId = JSON.parse(atob(token.split('.')[1])).userId;

        if (!shouldForceUserDashboardHomeNetwork()) {
          const raw = readHomeCache(userId)?.raw;
          if (raw && typeof raw.isChild === 'boolean') {
            setIsChild(raw.isChild);
            return;
          }
        }

        const response = await api.get(API.checkIfUserIsChild);
        if (response.data) {
          const isChildValue = response.data.isChild === true || 
                               response.data.isChild === 1 || 
                               response.data.isChild === '1' || 
                               response.data.isChild === 'true';
          const next = !!isChildValue;
          setIsChild(next);
          mergeDashboardRawFields(userId, { isChild: next });
        }
      } catch (error) {
        console.error('Error checking if user is child:', error);
        setIsChild(false);
      }
    };

    checkIfChild();
  }, []);

  const streakDiamondMetrics = {
    streakDays: streakForSummary.streakDays,
    diamondCount: dreameyDiamondsTotal,
    loading: streakForSummary.loading,
  };

  if (loading) {
    return (
      <Box
        sx={(t) => ({
          minHeight: '100%',
          background:
            t.palette.mode === 'dark'
              ? `radial-gradient(ellipse 120% 80% at 50% -20%, ${alpha(portal.cyan, 0.12)} 0%, transparent 50%),
                 radial-gradient(ellipse 80% 50% at 100% 50%, ${alpha(portal.purple, 0.08)} 0%, transparent 45%),
                 ${t.palette.background.default}`
              : `linear-gradient(180deg, ${alpha(portal.cyan, 0.06)} 0%, ${t.palette.background.default} 35%)`,
        })}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 }, pt: { xs: 1, sm: 2 } }}>
          <DashboardPageHeader
            icon={TrendingUp}
            title="My Summary"
            subtitle="Track your engagement insights, earnings progress, and account readiness in one place."
            sx={summaryHeaderSurfaceSx}
          />
        </Container>
        <WelcomeHeader user={{}} hideWelcomePromo streakDiamondMetrics={streakDiamondMetrics} />
        <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 } }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 3, color: 'text.primary', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
          Engagement Overview
          </Typography>
          <LoadingSkeleton type="earnings" count={4} />
        </Container>
      </Box>
    );
  }

  if (!summary) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography variant="h6" color="text.secondary">No engagement data available.</Typography>
      </Box>
    );
  }

  // Prepare middle content for WelcomeHeader (alert for Pakistan users)
  // Don't show this notice to child members
  const isPakistan = userProfile?.country?.toUpperCase() === 'PK' || userProfile?.countryCode?.toUpperCase() === 'PK';
  
  // Determine what's incomplete
  const profileIncomplete = isPakistan && !isProfileComplete && !isChild;
  const redemptionIncomplete = isPakistan && !hasCNIC && !isChild;
  const showAlert = profileIncomplete || redemptionIncomplete;
  
  // Build the appropriate message
  const getAlertMessage = () => {
    if (profileIncomplete && redemptionIncomplete) {
      return {
        prefix: 'Please update your ',
        linkText: 'Profile',
        linkTo: '/dashboard/profile',
        middle: ' and ',
        linkText2: 'Redemptions',
        linkTo2: '/dashboard/redemptions',
        suffix: ' as soon as possible for payments.',
        hasTwoLinks: true
      };
    } else if (profileIncomplete) {
      return {
        prefix: 'Please update your ',
        linkText: 'Profile',
        linkTo: '/dashboard/profile',
        suffix: ' as soon as possible.',
        hasTwoLinks: false
      };
    } else if (redemptionIncomplete) {
      return {
        prefix: 'Please update your ',
        linkText: 'Redemptions',
        linkTo: '/dashboard/redemptions',
        suffix: ' as soon as possible for payments.',
        hasTwoLinks: false
      };
    }
    return null;
  };
  
  const alertMessage = getAlertMessage();
  
  const middleContent = showAlert && alertMessage ? (
    <Alert 
      severity="warning" 
      sx={{ 
        fontSize: { xs: '0.9rem', sm: '1rem' },
        textAlign: 'center',
        '& .MuiAlert-message': {
          width: '100%',
          textAlign: 'center'
        }
      }}
    >
      <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5, textAlign: 'center', color: '#dc2626' }}>
        ⚠️ Important Notice
      </Typography>
      <Typography variant="body2" sx={{ textAlign: 'center' }}>
        {alertMessage.prefix}
        <Link 
          component="button"
          variant="body2"
          onClick={() => navigate(alertMessage.linkTo)}
          sx={{ 
            fontWeight: 600,
            textDecoration: 'underline',
            color: 'warning.dark',
            cursor: 'pointer'
          }}
        >
          {alertMessage.linkText}
        </Link>
        {alertMessage.hasTwoLinks && (
          <>
            {alertMessage.middle}
            <Link 
              component="button"
              variant="body2"
              onClick={() => navigate(alertMessage.linkTo2)}
              sx={{ 
                fontWeight: 600,
                textDecoration: 'underline',
                color: 'warning.dark',
                cursor: 'pointer'
              }}
            >
              {alertMessage.linkText2}
            </Link>
          </>
        )}
        {alertMessage.suffix}
      </Typography>
    </Alert>
  ) : null;

  const summaryWithBalance =
    availableBalance === null
      ? summary
      : { ...summary, availableBalance };

  return (
    <Box
      sx={(t) => ({
        minHeight: '100%',
        background:
          t.palette.mode === 'dark'
            ? `radial-gradient(ellipse 120% 80% at 50% -20%, ${alpha(portal.cyan, 0.12)} 0%, transparent 50%),
               radial-gradient(ellipse 80% 50% at 100% 50%, ${alpha(portal.purple, 0.08)} 0%, transparent 45%),
               ${t.palette.background.default}`
            : `linear-gradient(180deg, ${alpha(portal.cyan, 0.06)} 0%, ${t.palette.background.default} 35%)`,
      })}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 }, pt: { xs: 1, sm: 2 } }}>
        <DashboardPageHeader
          icon={TrendingUp}
          title="My Summary"
          subtitle="Track your engagement insights, earnings progress, and account readiness in one place."
          sx={summaryHeaderSurfaceSx}
        />
      </Container>
      <WelcomeHeader
        user={summaryWithBalance}
        middleContent={middleContent}
        hideWelcomePromo
        streakDiamondMetrics={streakDiamondMetrics}
      />
      
      <Container 
        maxWidth="lg" 
        sx={{ 
          px: { xs: 1, sm: 2 },
          pb: { xs: 2, sm: 4 },
          pt: { xs: 2, sm: 3 }
        }}
      >
        {/* KYC Status Indicator - Hidden as not integrated yet */}

        {/* Automatic Monthly Payments section removed - using new withdrawal system */}
      </Container>
    </Box>
  );
}

export default EarningsSummary;
